import { useEffect, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { Bell, Loader2, Globe, UserCircle2, AlarmClock, MessageSquare } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";

const ROLE_LABEL = { admin: "Amministratore", agente: "Agente", backoffice: "Backoffice" };

// Map icona dal nome ricevuto dal backend
const ICONS = { Globe, UserCircle2, AlarmClock, MessageSquare };
// Map colore tailwind → classi border/text/bg
const COLOR_CLASSES = {
  emerald: { border: "border-emerald-200", bg: "bg-emerald-50/60", text: "text-emerald-700", icon: "text-emerald-600" },
  indigo: { border: "border-indigo-200", bg: "bg-indigo-50/60", text: "text-indigo-700", icon: "text-indigo-600" },
  amber: { border: "border-amber-200", bg: "bg-amber-50/60", text: "text-amber-700", icon: "text-amber-600" },
  blue: { border: "border-blue-200", bg: "bg-blue-50/60", text: "text-blue-700", icon: "text-blue-600" },
};

export default function QuoteNotificationPrefs() {
  const [channels, setChannels] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState({}); // { `${userId}:${field}`: true }

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/notifications");
      setChannels(data?.channels || []);
      setUsers(data?.users || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore caricamento utenti");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const updatePref = async (userId, field, value) => {
    const key = `${userId}:${field}`;
    setUsers((prev) => prev.map((u) => u.id === userId ? { ...u, [field]: value } : u));
    setSaving((s) => ({ ...s, [key]: true }));
    try {
      await api.patch(`/admin/notifications/${userId}`, { [field]: value });
    } catch (e) {
      setUsers((prev) => prev.map((u) => u.id === userId ? { ...u, [field]: !value } : u));
      toast.error(e?.response?.data?.detail || "Errore aggiornamento");
    } finally {
      setSaving((s) => { const c = { ...s }; delete c[key]; return c; });
    }
  };

  const counts = channels.reduce((acc, c) => {
    const field = `notify_${c.key}`;
    acc[c.key] = users.filter((u) => u[field] && u.is_active !== false).length;
    return acc;
  }, {});

  return (
    <div className="space-y-6" data-testid="notifications-matrix-page">
      <div>
        <h1 className="font-display text-2xl font-bold flex items-center gap-2">
          <Bell className="h-6 w-6 text-blue-600" />
          Notifiche email — Matrice utenti / eventi
        </h1>
        <p className="text-sm text-slate-500 mt-1">
          Scegli chi del tuo staff riceverà email automatiche per ogni tipo di evento (richieste preventivo, scadenze polizze, messaggi clienti).
          Le impostazioni vengono salvate automaticamente al click. Solo utenti attivi ricevono email.
        </p>
      </div>

      {/* Card riepilogo per canale */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {channels.map((c) => {
          const Icon = ICONS[c.icon] || Bell;
          const col = COLOR_CLASSES[c.color] || COLOR_CLASSES.blue;
          return (
            <Card key={c.key} className={`${col.border} ${col.bg}`}>
              <CardContent className="pt-4 pb-4 flex items-center gap-3">
                <Icon className={`h-6 w-6 flex-shrink-0 ${col.icon}`} />
                <div className="min-w-0">
                  <div className={`text-[10px] uppercase tracking-wide ${col.text} font-semibold truncate`}>{c.label}</div>
                  <div className="text-xl font-bold" data-testid={`cnt-${c.key}`}>{counts[c.key] || 0}</div>
                  <div className="text-[10px] text-slate-500">utenti notificati</div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Staff dell&apos;agenzia ({users.length})</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center py-10 text-slate-400">
              <Loader2 className="h-5 w-5 animate-spin mr-2" /> Caricamento…
            </div>
          ) : users.length === 0 ? (
            <div className="text-center py-10 text-slate-400 text-sm">Nessun utente staff trovato.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 border-b border-slate-200 sticky top-0">
                  <tr>
                    <th className="text-left px-4 py-2.5 font-semibold text-slate-600 sticky left-0 bg-slate-50">Utente</th>
                    <th className="text-left px-3 py-2.5 font-semibold text-slate-600">Ruolo</th>
                    {channels.map((c) => {
                      const Icon = ICONS[c.icon] || Bell;
                      const col = COLOR_CLASSES[c.color] || COLOR_CLASSES.blue;
                      return (
                        <th key={c.key} className={`text-center px-3 py-2.5 font-semibold ${col.text} whitespace-nowrap min-w-[140px]`}>
                          <Icon className="inline h-3.5 w-3.5 mr-1 -mt-0.5" />
                          {c.label}
                        </th>
                      );
                    })}
                  </tr>
                </thead>
                <tbody>
                  {users.map((u) => {
                    const inactive = u.is_active === false;
                    return (
                      <tr key={u.id} className={`border-t border-slate-100 ${inactive ? "opacity-50" : "hover:bg-slate-50/50"}`} data-testid={`notif-row-${u.id}`}>
                        <td className="px-4 py-2.5 sticky left-0 bg-white">
                          <div className="font-medium">{u.name}</div>
                          <div className="text-[11px] text-slate-500">{u.email}</div>
                          {inactive && <span className="text-[10px] uppercase text-rose-600 font-bold">Disattivato</span>}
                        </td>
                        <td className="px-3 py-2.5">
                          <Badge variant="outline" className="font-normal">{ROLE_LABEL[u.role] || u.role}</Badge>
                        </td>
                        {channels.map((c) => {
                          const field = `notify_${c.key}`;
                          const key = `${u.id}:${field}`;
                          return (
                            <td key={c.key} className="text-center px-3 py-2.5">
                              <Checkbox
                                data-testid={`notif-${c.key}-${u.id}`}
                                checked={!!u[field]}
                                disabled={inactive || !!saving[key]}
                                onCheckedChange={(v) => updatePref(u.id, field, !!v)}
                              />
                            </td>
                          );
                        })}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      <p className="text-xs text-slate-400">
        Suggerimento: lascia almeno 1 amministratore con la spunta attiva per ogni canale, per non perdere notifiche.
        Le caselle disabilitate appartengono a utenti disattivati.
      </p>
    </div>
  );
}
